import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, Settings, UserCheck, UserX, GraduationCap } from 'lucide-react';
import { Gate } from '../../AccessStudio';
import SectionManagerModal from './SectionManagerModal';
import TeacherAssignmentsModal from './TeacherAssignmentsModal';
import useScopedClasses from '../useScopedClasses';

// ============================================================
// Roster: assign a class's students into sections, then filter
// and browse by class/section. Assignment writes
// StudentAcademicRecords.Section (per-year — history untouched).
// ============================================================

const RosterView = ({ apiCall, year, hasPermission, showSuccess, showError }) => {
  const { visibleClasses, className, setClassName } = useScopedClasses(apiCall, year);
  const [sections, setSections] = useState([]);
  const [roster, setRoster] = useState([]);
  const [sectionFilter, setSectionFilter] = useState('all'); // all | unassigned | <code>
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]); // StudentIDs
  const [targetSection, setTargetSection] = useState('');
  const [showManager, setShowManager] = useState(false);
  const [showTeachers, setShowTeachers] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadSections = useCallback(async () => {
    if (!className) return;
    try {
      setSections(await apiCall(`/academic/sections?academicYear=${year}&className=${encodeURIComponent(className)}`));
    } catch (err) { showError('Failed to load sections: ' + err.message); }
  }, [apiCall, year, className, showError]);

  const loadRoster = useCallback(async () => {
    if (!className) return;
    try {
      setLoading(true);
      setRoster(await apiCall(`/academic/roster?academicYear=${year}&className=${encodeURIComponent(className)}`));
      setSelected([]);
    } catch (err) {
      showError('Failed to load roster: ' + err.message);
    } finally { setLoading(false); }
  }, [apiCall, year, className, showError]);

  useEffect(() => { loadSections(); loadRoster(); setSectionFilter('all'); }, [loadSections, loadRoster]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return roster.filter(s => {
      if (sectionFilter === 'unassigned' && s.Section) return false;
      if (sectionFilter !== 'all' && sectionFilter !== 'unassigned' && s.Section !== sectionFilter) return false;
      if (q && !(`${s.FirstName} ${s.LastName}`.toLowerCase().includes(q) || (s.StudentID || '').toLowerCase().includes(q))) return false;
      return true;
    });
  }, [roster, sectionFilter, search]);

  const unassignedCount = roster.filter(s => !s.Section).length;

  const toggle = (id) => setSelected(sel => sel.includes(id) ? sel.filter(x => x !== id) : [...sel, id]);
  const toggleAllVisible = () => {
    const ids = visible.map(s => s.StudentID);
    const allIn = ids.every(id => selected.includes(id));
    setSelected(allIn ? selected.filter(id => !ids.includes(id)) : [...new Set([...selected, ...ids])]);
  };

  const assign = async (code) => {
    if (!selected.length) { showError('Select students first'); return; }
    if (code && !sections.some(s => s.SectionCode === code && s.IsActive)) { showError('Pick a section'); return; }
    try {
      const r = await apiCall('/academic/roster/assign', {
        method: 'PUT',
        body: JSON.stringify({ academicYear: year, className, sectionCode: code || null, studentIds: selected })
      });
      showSuccess(r.message);
      loadRoster(); loadSections();
    } catch (err) { showError(err.message); }
  };

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="bg-white rounded-lg shadow p-4 flex flex-wrap items-center gap-3">
        <select
          value={className}
          onChange={e => setClassName(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm"
        >
          {visibleClasses.length === 0 && <option value="">No classes</option>}
          {visibleClasses.map(cl => <option key={cl.ClassName} value={cl.ClassName}>{cl.ClassName}</option>)}
        </select>

        <div className="relative flex-1 min-w-[180px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search student..."
            className="w-full border rounded-lg pl-9 pr-3 py-2 text-sm"
          />
        </div>

        <Gate permission="academic.roster.configure" label="Manage Sections">
          <button
            onClick={() => setShowManager(true)}
            className="px-3 py-2 border border-blue-300 text-blue-700 text-sm rounded-lg hover:bg-blue-50 flex items-center gap-1"
          >
            <Settings size={15} /> Sections
          </button>
        </Gate>
        <Gate permission="academic.roster.configure" label="Manage Teacher Assignments">
          <button
            onClick={() => setShowTeachers(true)}
            className="px-3 py-2 border border-blue-300 text-blue-700 text-sm rounded-lg hover:bg-blue-50 flex items-center gap-1"
          >
            <GraduationCap size={15} /> Teachers
          </button>
        </Gate>
      </div>

      {/* Section chips */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setSectionFilter('all')}
          className={`px-3 py-1 rounded-full text-sm ${sectionFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-white border text-gray-700'}`}
        >
          All ({roster.length})
        </button>
        <button
          onClick={() => setSectionFilter('unassigned')}
          className={`px-3 py-1 rounded-full text-sm ${sectionFilter === 'unassigned' ? 'bg-amber-500 text-white' : 'bg-white border text-gray-700'}`}
        >
          Unassigned ({unassignedCount})
        </button>
        {sections.filter(s => s.IsActive).map(s => (
          <button
            key={s.SectionID}
            onClick={() => setSectionFilter(s.SectionCode)}
            className={`px-3 py-1 rounded-full text-sm ${sectionFilter === s.SectionCode ? 'bg-blue-600 text-white' : 'bg-white border text-gray-700'}`}
            title={s.HomeroomTeacherName ? `Homeroom: ${s.HomeroomTeacherName}` : undefined}
          >
            {className} {s.SectionCode} ({s.StudentCount})
          </button>
        ))}
      </div>

      {/* Assign bar */}
      <Gate permission="academic.roster.edit" label="Assign Students to Section">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex flex-wrap items-center gap-2 text-sm">
          <span className="font-medium text-blue-900">{selected.length} selected</span>
          <select
            value={targetSection}
            onChange={e => setTargetSection(e.target.value)}
            className="border rounded-lg px-2 py-1.5 text-sm"
          >
            <option value="">— section —</option>
            {sections.filter(s => s.IsActive).map(s => (
              <option key={s.SectionID} value={s.SectionCode}>{className} {s.SectionCode}</option>
            ))}
          </select>
          <button
            onClick={() => assign(targetSection)}
            disabled={!selected.length || !targetSection}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
          >
            <UserCheck size={14} /> Assign
          </button>
          <button
            onClick={() => assign(null)}
            disabled={!selected.length}
            className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1"
          >
            <UserX size={14} /> Unassign
          </button>
        </div>
      </Gate>

      {/* Roster table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <p className="text-center text-gray-500 py-8">Loading roster...</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-3 py-2 w-8">
                  <input type="checkbox"
                    checked={visible.length > 0 && visible.every(s => selected.includes(s.StudentID))}
                    onChange={toggleAllVisible} />
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Gender</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Section</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visible.map(s => (
                <tr key={s.StudentID}
                    className={`hover:bg-gray-50 cursor-pointer ${selected.includes(s.StudentID) ? 'bg-blue-50' : ''}`}
                    onClick={() => toggle(s.StudentID)}>
                  <td className="px-3 py-1.5">
                    <input type="checkbox" checked={selected.includes(s.StudentID)} readOnly />
                  </td>
                  <td className="px-3 py-1.5 font-medium text-gray-800">{s.FirstName} {s.LastName}</td>
                  <td className="px-3 py-1.5 text-gray-500">{s.StudentID}</td>
                  <td className="px-3 py-1.5 text-center text-gray-500">{s.Gender || '—'}</td>
                  <td className="px-3 py-1.5 text-center">
                    {s.Section
                      ? <span className="inline-flex px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800">{s.Section}</span>
                      : <span className="inline-flex px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">unassigned</span>}
                  </td>
                </tr>
              ))}
              {visible.length === 0 && (
                <tr><td colSpan="5" className="text-center text-gray-500 py-6">No students match.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {showTeachers && (
        <TeacherAssignmentsModal
          apiCall={apiCall}
          year={year}
          onClose={() => setShowTeachers(false)}
          showSuccess={showSuccess}
          showError={showError}
        />
      )}

      {showManager && (
        <SectionManagerModal
          apiCall={apiCall}
          year={year}
          className={className}
          onClose={() => setShowManager(false)}
          onChanged={() => { loadSections(); loadRoster(); }}
          showSuccess={showSuccess}
          showError={showError}
        />
      )}
    </div>
  );
};

export default RosterView;
